import React, { useState, useEffect, useRef } from "react";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Heading from "@amzn/meridian/heading";
import Alert from "@amzn/meridian/alert";
import { useAppSelector } from "../../store";
import { selectCurrentThemeMode } from "../../store/slices/themeSlice";
import raptorSvg from "../../assets/images/raptor.svg";
import "./RatesRosterLoader.scss";

// Theme palette colors from customTheme.json (updated deeper purple)
const PALETTE = {
  light: { bg: '#c59bff', bar: '#6636a6' },   // palette200, palette500
  dark:  { bg: '#8456c6', bar: '#9e6fe1' },    // palette400, palette300
};

interface RatesRosterLoaderProps {
  type: "roster" | "rates" | "volumes" | "plan";
  status: "loading" | "success" | "error";
  message?: string;
  durationSeconds?: number;
}

/** Helpful messages shown at different progress milestones */
const LOADING_MESSAGES: Record<string, string[]> = {
  roster: [
    "Fetching inputs for your shift from multiple systems...",
    "Loading roster data, attendance and rate configurations...",
    "Processing adjustments and override calculations...",
    "Almost there, finalizing roster and rates data...",
  ],
  rates: [
    "Fetching Trailer Data from multiple systems...",
    "Loading volume data by trailer...",
    "Finalizing Trailer recommendations...",
  ],
  volumes: [
    "Loading volume targets and buffer data...",
    "Calculating buffer distributions across process paths...",
    "Processing process path volume data...",
    "Finalizing volume and buffer data...",
  ],
  plan: [
    "Loading shift plan output data...",
    "Processing primary grading volumes and projections...",
    "Calculating shift totals and metrics...",
    "Finalizing shift plan review data...",
  ],
};

/**
 * Circular progress ring component (SVG-based)
 */
const CircularProgress: React.FC<{ percent: number; isDark?: boolean }> = ({ percent, isDark = false }) => {
  const colors = isDark ? PALETTE.dark : PALETTE.light;
  const radius = 54;
  const stroke = 6;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: radius * 2, height: radius * 2, flexShrink: 0 }}>
      <svg width={radius * 2} height={radius * 2} className="circular-progress">
        {/* Background circle - theme palette color */}
        <circle
          stroke={colors.bg}
          opacity={isDark ? 0.3 : 0.5}
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* Progress arc - theme palette color */}
        <circle
          stroke={colors.bar}
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          style={{
            transition: "stroke-dashoffset 0.5s ease-in-out",
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
          }}
        />
      </svg>
      {/* Percentage text - using Meridian Text for proper dark mode support */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        lineHeight: 1,
      }}>
        <Text type="b400">{Math.round(percent)}%</Text>
      </div>
    </div>
  );
};

/**
 * Loading component with Raptor SVG illustration, circular progress indicator,
 * percentage display, and helpful messages.
 * 
 * Assumes ~60 second delay for rates/roster and review page loading.
 * Shows contextual messages and warnings about not reloading.
 */
const RatesRosterLoader: React.FC<RatesRosterLoaderProps> = ({
  type,
  status,
  message,
  durationSeconds = 60,
}) => {
  const themeMode = useAppSelector(selectCurrentThemeMode);
  const isDark = themeMode === 'dark';
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (status !== "loading") {
      setProgress(100);
      return;
    }

    setProgress(0);
    setMessageIndex(0);

    // Update progress every 500ms
    const stepMs = 500;
    const totalSteps = (durationSeconds * 1000) / stepMs;
    let currentStep = 0;

    intervalRef.current = setInterval(() => {
      currentStep++;
      // Cap at 95% — the final 5% completes when data actually arrives
      const newProgress = Math.min((currentStep / totalSteps) * 100, 95);
      setProgress(newProgress);

      // Update message at 25% intervals
      const messages = LOADING_MESSAGES[type] || LOADING_MESSAGES.plan;
      const newMsgIndex = Math.min(
        Math.floor(newProgress / 25),
        messages.length - 1
      );
      setMessageIndex(newMsgIndex);

      if (newProgress >= 95) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, stepMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [status, type, durationSeconds]);

  const messages = LOADING_MESSAGES[type] || LOADING_MESSAGES.plan;
  const currentMessage = message || messages[messageIndex];

  if (status === "success") {
    return (
      <div className="loader-fullscreen">
        <Box className="loader-content">
          <div className="loader-success">
            <img
              src={raptorSvg}
              alt="ReLo Raptor"
              className="loader-raptor loader-raptor--small"
            />
            <Text type="b200" color="success">
              {message ||
                `${type.charAt(0).toUpperCase() + type.slice(1)} data loaded successfully`}
            </Text>
          </div>
        </Box>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="loader-fullscreen">
        <Box className="loader-content">
          <div className="loader-error">
            <Text type="b200" color="error">
              {message || `Error loading ${type} data`}
            </Text>
          </div>
        </Box>
      </div>
    );
  }

  // Loading state
  return (
    <div className="loader-fullscreen">
      {/* Decorative background shapes */}
      <div className="loader-decorations">
        <div className="loader-decoration loader-decoration--swoosh1" />
        <div className="loader-decoration loader-decoration--circle1" />
        <div className="loader-decoration loader-decoration--circle2" />
        <div className="loader-decoration loader-decoration--blob1" />
      </div>

      <Box className="loader-content">
        {/* Raptor illustration */}
        <div className="loader-raptor-container">
          <img
            src={raptorSvg}
            alt="ReLo Raptor - Loading"
            className="loader-raptor"
          />
        </div>

        {/* Progress indicator */}
        <div className="loader-progress-section">
          <CircularProgress percent={progress} isDark={isDark} />

          <div className="loader-messages">
            <Heading level={3} type="h300">
              {currentMessage}
            </Heading>

            <Text type="b200" color="secondary">
              {type === 'roster' 
                ? "Inputs for your shift are being fetched from multiple systems — please wait"
                : type === 'plan'
                ? "Generating shift plan output — please wait"
                : type === 'volumes'
                ? "Loading volume targets and buffer data — please wait"
                : `Loading ${type} data — please wait`}
            </Text>
          </div>
        </div>

        {/* Warning message */}
        <div className="loader-warning">
          <Alert type="neutral" size="large">
            Please do not reload this page. Refreshing could cause the system to
            reset or provide inconsistent results. Your inputs are being
            processed.
          </Alert>
        </div>
      </Box>
    </div>
  );
};

export default RatesRosterLoader;

import './RatesRosterLoader.scss';